import React, { useState, useEffect } from 'react';
import { FileText, Download, Calendar, Euro, Package } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../LoadingSpinner';

const CustomerInvoices = () => {
  const { token } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      loadInvoices();
    }
  }, [token]);

  const loadInvoices = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/orders/customer/invoices`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setInvoices(data.invoices || []);
        }
      }
    } catch (error) {
      console.error('Errore caricamento fatture:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDocumentTypeLabel = (type) => {
    return type === 'invoice' ? 'Fattura' : 'Ricevuta';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('it-IT', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return <LoadingSpinner message="Caricamento fatture e ricevute..." />;
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Fatture e Ricevute</h2>

      {invoices.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <FileText className="h-16 w-16 mx-auto mb-4 text-gray-400" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Nessun Documento Disponibile
          </h3>
          <p className="text-gray-600">
            Al momento non ci sono fatture o ricevute disponibili per i tuoi ordini.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {invoices.map((invoice) => (
            <div
              key={invoice.id}
              className="bg-white rounded-lg shadow hover:shadow-md transition-shadow p-6"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4 flex-1">
                  <div className="p-3 bg-red-50 rounded-lg">
                    <FileText className="h-8 w-8 text-red-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {invoice.fileName}
                      </h3>
                      <span className="px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded">
                        {getDocumentTypeLabel(invoice.type)}
                      </span>
                    </div>

                    {invoice.invoiceNumber && (
                      <p className="text-sm text-gray-600 mb-1">
                        Numero: <span className="font-medium">{invoice.invoiceNumber}</span>
                      </p>
                    )}

                    {invoice.notes && (
                      <p className="text-sm text-gray-500 italic mb-2">{invoice.notes}</p>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3 text-sm">
                      <div className="flex items-center space-x-2 text-gray-600">
                        <Package className="h-4 w-4" />
                        <span>Ordine: <span className="font-medium">{invoice.orderNumber}</span></span>
                      </div>
                      <div className="flex items-center space-x-2 text-gray-600">
                        <Calendar className="h-4 w-4" />
                        <span>{formatDate(invoice.orderDate)}</span>
                      </div>
                      <div className="flex items-center space-x-2 text-gray-600">
                        <Euro className="h-4 w-4" />
                        <span className="font-medium">€{invoice.orderTotal?.toFixed(2)}</span>
                      </div>
                    </div>

                    <p className="text-xs text-gray-400 mt-3">
                      Caricato il {formatDate(invoice.uploadedAt)}
                    </p>
                  </div>
                </div>

                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/upload/s3/${invoice.s3Key}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-4 flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  <Download className="h-4 w-4" />
                  <span>Scarica</span>
                </a>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CustomerInvoices;
